import React, { useState } from 'react';
import { FaMicrophone, FaMicrophoneSlash } from 'react-icons/fa';
import swal from 'sweetalert';
import { useAudioRecorder } from './useAudioRecorder';
import { captureVoiceOpVisit, mapVoiceVisitToFormPatch } from './voiceOpVisitApi';
import './VoiceOpVisitCapture.css';

/**
 * VoiceOpVisitCapture — Top-level button and flow for voice-based Op Visit prefill.
 *
 * Props:
 *   patientId         - Patient ID for the API call
 *   formData          - Current form state
 *   setFormData       - State setter for formData
 *   setSelectedTests  - State setter for selectedTests (synced to formData.testsRequested)
 *   medicinesArr      - Array of available medicines (for master-list matching)
 *   calculateBMIAndBSA - Function to recalc BMI/BSA after vitals merge
 *   updatePatientHeightWeight - Function to sync height/weight to patient master
 */
export default function VoiceOpVisitCapture({
  patientId,
  formData,
  setFormData,
  setSelectedTests,
  medicinesArr = [],
  calculateBMIAndBSA = () => {},
  updatePatientHeightWeight = () => {},
}) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [showTranscript, setShowTranscript] = useState(false);

  const handleRecordingComplete = async (audioBlob) => {
    setIsProcessing(true);
    setTranscript('');
    setShowTranscript(false);

    try {
      const result = await captureVoiceOpVisit(patientId, audioBlob, 'unknown');

      if (result.serviceStatus === 'SUCCESS' && result.data) {
        const { visit, transcript: apiTranscript, voiceCaptureId } = result.data;

        // Show the transcript
        setTranscript(apiTranscript || '');
        setShowTranscript(true);

        // Map API response to form patch
        const { patch, diffList, flaggedMedicines } = mapVoiceVisitToFormPatch(visit, formData, medicinesArr);

        // If no differences, apply immediately and show success
        if (diffList.length === 0 && flaggedMedicines.length === 0) {
          applyPatch(patch);
          swal({
            title: 'Voice capture successful!',
            text: 'Form fields pre-filled with captured data.',
            icon: 'success',
            buttons: false,
            timer: 3000,
          });
        } else {
          // Show confirmation dialog listing what will be overwritten
          const diffText = diffList.map((d) => `• ${d.label}`).join('\n');
          const flagText =
            flaggedMedicines.length > 0
              ? `\n\nMedicines with notes:\n${flaggedMedicines.map((f) => `  • ${f.reason}`).join('\n')}`
              : '';

          const confirmText =
            diffList.length > 0
              ? `The following fields will be overwritten:\n\n${diffText}${flagText}`
              : `Some medicines have notes (see below). Proceed with prefill?\n${flagText}`;

          swal({
            title: 'Confirm prefill',
            text: confirmText,
            icon: 'warning',
            buttons: ['Cancel', 'Yes, overwrite'],
            dangerMode: true,
          }).then((confirmed) => {
            if (confirmed) {
              applyPatch(patch);
              swal({
                title: 'Form pre-filled!',
                text: 'Review the fields and complete any missing data before saving.',
                icon: 'success',
                buttons: false,
                timer: 3000,
              });
            }
          });
        }
      } else {
        // Handle API errors per the spec's error-message table
        let userMessage = result.errorMsg || 'Unknown error';

        // Map backend error codes to user-friendly messages (spec §Error Messages)
        const errorCodeMap = {
          DISABLED: 'Op-Visit voice capture is not enabled on this server.',
          INVALID_AUDIO: 'The audio file format is not supported or the file is empty.',
          FILE_TOO_LARGE: 'The audio file exceeds the maximum allowed size (35 MB).',
          BAD_REQUEST: 'No audio file provided, or unsupported audio format.',
          NO_SPEECH_DETECTED: 'No speech was detected in the audio recording.',
          TIMEOUT: 'The voice capture took too long and was stopped.',
          RATE_LIMITED: 'The voice capture service is busy. Please try again shortly.',
          UPSTREAM_ERROR: 'The voice capture service reported an internal error.',
          SERVICE_UNAVAILABLE: 'The voice capture service could not be reached. Please try again later.',
          UNEXPECTED: 'The voice capture could not be completed.',
        };

        // Attempt to parse error code from errorMsg (some backends prefix it)
        const firstWord = result.errorMsg?.split(/[\s:]/)[0] || '';
        if (errorCodeMap[firstWord]) {
          userMessage = errorCodeMap[firstWord];
        }

        // Retry button for transient errors
        const isRetryable = ['RATE_LIMITED', 'TIMEOUT', 'UPSTREAM_ERROR', 'SERVICE_UNAVAILABLE'].includes(
          firstWord
        );

        if (isRetryable) {
          swal({
            title: 'Capture failed (retryable)',
            text: userMessage,
            icon: 'warning',
            buttons: ['OK', 'Try again'],
          }).then((shouldRetry) => {
            if (shouldRetry) {
              // User clicked "Try again" — just close the alert; they can click Start again
            }
          });
        } else {
          swal({
            title: 'Capture failed',
            text: userMessage,
            icon: 'error',
            buttons: false,
            timer: 5000,
          });
        }
      }
    } catch (err) {
      swal({
        title: 'Error',
        text: `Failed to process voice capture: ${err.message}`,
        icon: 'error',
        buttons: false,
        timer: 5000,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const applyPatch = (patch) => {
    // Extract selectedTests if present (maps to setSelectedTests separately)
    const { selectedTests, ...formPatch } = patch;

    // Apply form patch
    setFormData((prev) => ({
      ...prev,
      ...formPatch,
    }));

    // Apply selectedTests if present
    if (selectedTests) {
      setSelectedTests(selectedTests);
    }

    // Trigger vitals side effects (height/weight sync, BMI/BSA recalc)
    if (formPatch.vitals) {
      setTimeout(() => {
        updatePatientHeightWeight(formPatch.vitals[0].height || '', formPatch.vitals[0].weight || '');
        calculateBMIAndBSA(0);
      }, 100);
    }
  };

  const { start, stop, isRecording, timeLeft, error: micError } = useAudioRecorder({
    onRecordingComplete: handleRecordingComplete,
    maxDurationMs: 30000,
  });

  // Graceful degradation: if MediaRecorder unsupported, don't render the button
  if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices) {
    return null;
  }

  return (
    <div className="voice-op-visit-capture">
      <div className="voice-capture-buttons">
        <button
          type="button"
          className={`btn ${isRecording ? 'btn-danger' : 'btn-secondary'}`}
          onClick={isRecording ? stop : start}
          disabled={isProcessing}
          title={isRecording ? 'Stop recording (max 30s)' : 'Start voice capture (30s max)'}
        >
          {isRecording ? (
            <>
              <FaMicrophoneSlash /> Stop ({Math.ceil(timeLeft)}s)
            </>
          ) : isProcessing ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Processing…
            </>
          ) : (
            <>
              <FaMicrophone /> Start Voice Capture
            </>
          )}
        </button>
      </div>

      {micError && <div className="alert alert-danger mt-2 mb-2" style={{marginTop: '10px'}}>{micError}</div>}

      {showTranscript && transcript && (
        <div className="alert alert-info mt-2 mb-2" style={{marginTop: '10px'}}>
          <strong>Transcript:</strong>
          <p className="mb-1">{transcript}</p>
          <button
            type="button"
            className="btn btn-sm btn-link p-0 text-decoration-none"
            onClick={() => setShowTranscript(false)}
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}
